import { z } from 'zod';
import { t } from 'i18next';
import { InputRegexMap, OnboardingFormFields } from '@Types/index.ts';
import type { Country } from '@Types/country';

export const zipSchema = z.object({
  [OnboardingFormFields.ZIP]: z
    .string()
    .trim()
    .describe('zip')
    .min(1, { message: t('enErrors:required') })
    .regex(InputRegexMap.zipCode, t('enErrors:address.zipInvalid'))
    .min(5, { message: t('enErrors:address.zip') })
    .max(5, { message: t('enErrors:address.zip') }),
});

export const zipSchemaWNonUs = z.object({
  [OnboardingFormFields.ZIP]: z
    .string()
    .trim()
    .describe('zip')
    .min(1, { message: t('enErrors:required') }),
});

export const getZipSchemeByCountry = (country: Country | undefined) => {
  return z.object({
    [OnboardingFormFields.ZIP]: z
      .string()
      .trim()
      .describe('zip')
      .min(1, { message: t('enErrors:required') })
      .regex(
        new RegExp(country?.postalCodeValidationRegex || ''),
        country?.postalCodeValidationDescription || ''
      ),
  });
};

export const getZipValidations = (country: Country) => {
  let zipRegex: RegExp;

  if (typeof country.postalCodeValidationRegex === 'string') {
    zipRegex = new RegExp(country.postalCodeValidationRegex);
  } else {
    zipRegex = InputRegexMap.zipCode;
  }
  return z.string().min(1, 'req').regex(zipRegex, country.postalCodeValidationDescription);
};

export const nonUsRefineZip = <T extends NonUsSuperRefineZipData>(schema: z.ZodType<T>) =>
  schema.superRefine((data, ctx) => {
    if (data[OnboardingFormFields.COUNTRY]) {
      const zipSchemaValidation = getZipValidations(data[OnboardingFormFields.COUNTRY]);
      const zipResult = zipSchemaValidation.safeParse(data[OnboardingFormFields.ZIP]);
      if (!zipResult.success) {
        zipResult.error.issues.forEach((issue) => {
          ctx.addIssue({
            code: 'custom',
            message: issue.message,
            path: [OnboardingFormFields.ZIP],
          });
        });
      }
    }
  });

export interface NonUsSuperRefineZipData {
  [OnboardingFormFields.COUNTRY]?: Country | null | undefined;
  [OnboardingFormFields.ZIP]: string;
}

export const nonUsSuperRefineZip = (data: NonUsSuperRefineZipData, ctx: z.RefinementCtx) => {
  if (data[OnboardingFormFields.COUNTRY]) {
    const zipSchemaValidation = getZipValidations(data[OnboardingFormFields.COUNTRY]);
    const zipResult = zipSchemaValidation.safeParse(data[OnboardingFormFields.ZIP]);
    if (!zipResult.success) {
      zipResult.error.issues.forEach((issue) => {
        ctx.addIssue({ code: 'custom', message: issue.message, path: [OnboardingFormFields.ZIP] });
      });
    }
  }
};
